import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SecurityContext = createContext();

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [sessionTimeout, setSessionTimeout] = useState(null);

  // Logout function - defined early to avoid circular dependency
  const logout = useCallback(async () => {
    // Note: Our Node.js backend doesn't have a logout endpoint
    // The session is managed client-side with JWT expiration
    // We just clear the local storage and state
    
    setIsAuthenticated(false);
    setUserData(null);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userData');
    
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
      setSessionTimeout(null);
    }
  }, [sessionTimeout]);

  // Check authentication status on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const userInfo = localStorage.getItem('userData');
    
    if (authStatus === 'true' && userInfo) {
      const userData = JSON.parse(userInfo);
      
      // Verify token with backend
      verifyTokenWithServer(userData.accessToken).then(isValid => {
        if (isValid) {
          setIsAuthenticated(true);
          setUserData(userData);
          
          // Set session timeout (30 minutes)
          const timeout = setTimeout(() => {
            logout();
          }, 30 * 60 * 1000);
          
          setSessionTimeout(timeout);
        } else {
          // Token is invalid, clear storage
          logout();
        }
      }).catch(() => {
        // Error verifying token, clear storage
        logout();
      });
    }
  }, [logout]);

  // Verify token with backend 
  const verifyTokenWithServer = async (token) => {
    try {
      const API_BASE_URL = 'https://consoletz.bwitonline.com/api';
      const response = await fetch(`${API_BASE_URL}/verify-token?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        return false;
      }
      
      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error verifying token:', error);
      return false;
    }
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
      }
    };
  }, [sessionTimeout]);

  // Login function
  const login = (userInfo) => {
    setIsAuthenticated(true);
    setUserData(userInfo);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userData', JSON.stringify(userInfo));
    
    // Reset session timeout
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
    }
    
    const timeout = setTimeout(() => {
      logout();
    }, 30 * 60 * 1000);
    
    setSessionTimeout(timeout);
  };


  // Extend session
  const extendSession = () => {
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
    }
    
    const timeout = setTimeout(() => {
      logout();
    }, 30 * 60 * 1000);
    
    setSessionTimeout(timeout);
  };

  // Security check function
  const securityCheck = () => {
    // Additional security checks can be added here
    return isAuthenticated;
  };

  const value = {
    isAuthenticated,
    userData,
    login,
    logout,
    extendSession,
    securityCheck
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};
